// 登录页 — 基于 gitman/web 的 LoginPage（Fluent UI v9）并与 mailman 后端能力对齐：
//   - 集成滑块拼图验证码：点击「登录」先校验用户名/密码，通过后弹出 TeachingPopover
//     展示滑块验证码，完成拼图后点「确认登录」真正提交（与 ming-git 登录页一致）。
//   - 保留：卡片布局、HelpPopover 说明、SSO 圆形按钮（点击整页跳 IdP 授权页）
import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Label,
  Card,
  Spinner,
  Title3,
} from '@fluentui/react-components';
import { Scale } from '@fluentui/react-motion-components-preview';
import {
  TeachingPopover,
  TeachingPopoverTrigger,
  TeachingPopoverSurface,
  TeachingPopoverBody,
  TeachingPopoverTitle,
} from '@fluentui/react-teaching-popover';
import {
  PersonRegular,
  LockClosedRegular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { DEFAULT_REDIRECT_AFTER_LOGIN } from '@/routes';
import { ApiError } from '@/api/client';
import { authApi, type CaptchaChallenge } from '@/api/auth';
import { oauthApi } from '@/api/oauth'
import type { LoginConfig, OAuthProviderPublic } from '@/api/oauth'
import { HelpPopover } from '@/components/HelpPopover';
import { SliderCaptcha } from '@/components/SliderCaptcha';

const useStyles = makeStyles({
  fullPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Fluent UI Card 内置 width:fit-content 会覆盖 CSS 类的 width,只能用 inline style 强制
  card: {
    maxWidth: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow16,
  },
  cardBody: {
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  title: {
    textAlign: 'center',
    fontWeight: 600,
  },
  // 窄屏:logo + 标题纵向居中;桌面 ≥720px:横向排列
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    marginBottom: tokens.spacingVerticalM,
    '@media (min-width: 720px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacingHorizontalM,
    },
  },
  logo: {
    width: '56px',
    height: '56px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalXS,
  },
  fieldError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  submitButton: {
    width: '100%',
    marginTop: tokens.spacingVerticalXS,
  },
  // TeachingPopover 内容区:验证码 + 操作按钮
  popoverSurface: {
    width: '300px',
    maxWidth: '90vw',
  },
  popoverActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
  },
  // OAuth 圆形按钮(紧凑,居中)
  ssoRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  ssoCircle: {
    width: '52px',
    height: '52px',
    minWidth: '52px',
    padding: 0,
    flexShrink: 0,
  },
  ssoCircleLabel: {
    fontWeight: 600,
  },
});

export function LoginPage() {
  const styles = useStyles();
  const { login, isAuthenticated, loading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [loginConfig, setLoginConfig] = useState<LoginConfig>({
    local_login_enabled: true,
    oauth_enabled: true,
    providers: [],
  });

  // 本地登录表单
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    usernameOrEmail?: string;
    password?: string;
    captcha?: string;
  }>({});
  const [error, setError] = useState<string | null>(null);

  // 滑块验证码状态
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaX, setCaptchaX] = useState(0);

  // TeachingPopover 受控开关:点击「登录」校验通过后打开
  const [captchaOpen, setCaptchaOpen] = useState(false);

  // 登录卡片入场动画:挂载后从 false→true 触发 Scale 缩放进入
  const [cardVisible, setCardVisible] = useState(false);
  useEffect(() => {
    setCardVisible(true);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await oauthApi.getLoginConfig();
        setLoginConfig(cfg);
      } catch {
        // 配置不可用时静默（默认允许本地登录）
      }
    })();
  }, []);

  // 验证码按需加载:popover 打开时才拉取,不再页面加载时自动请求
  async function refreshCaptcha() {
    setCaptchaLoading(true);
    try {
      const challenge = await authApi.captcha();
      setCaptcha(challenge);
      setCaptchaX(0);
      setFieldErrors((prev) => ({ ...prev, captcha: undefined }));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '获取验证码失败，请稍后重试';
      setFieldErrors((prev) => ({ ...prev, captcha: msg }));
    } finally {
      setCaptchaLoading(false);
    }
  }

  if (isAuthenticated()) {
    const from =
      (location.state as { from?: string } | null)?.from ?? DEFAULT_REDIRECT_AFTER_LOGIN;
    return <Navigate to={from} replace />;
  }

  // 仅校验用户名 + 密码(captcha 在 popover 里校验)
  function validateCredentials() {
    const errs: { usernameOrEmail?: string; password?: string } = {};
    if (!usernameOrEmail.trim()) errs.usernameOrEmail = '请输入用户名或邮箱';
    if (!password) errs.password = '请输入密码';
    setFieldErrors((prev) => ({ ...prev, ...errs }));
    return !errs.usernameOrEmail && !errs.password;
  }

  // 尝试打开验证码 popover:先校验凭据,通过才打开并加载验证码
  function tryOpenCaptcha() {
    if (validateCredentials()) {
      setCaptchaOpen(true);
      if (!captcha) refreshCaptcha();
    }
  }

  // TeachingPopover onOpenChange:打开时走凭据校验,关闭时直接关
  function handleCaptchaOpenChange(_: unknown, data: { open: boolean }) {
    if (data.open) {
      tryOpenCaptcha();
    } else {
      setCaptchaOpen(false);
    }
  }

  // Enter 键提交:校验通过后打开验证码 popover
  function handleEnterKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryOpenCaptcha();
    }
  }

  // 真正执行登录(popover 里「确认登录」按钮调用)
  async function doLogin() {
    if (!captcha) {
      setFieldErrors((prev) => ({ ...prev, captcha: '请先完成滑块验证' }));
      return;
    }
    setError(null);
    try {
      await login(usernameOrEmail.trim(), password, captcha.token, captchaX);
      const from =
        (location.state as { from?: string } | null)?.from ?? DEFAULT_REDIRECT_AFTER_LOGIN;
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '登录失败，请稍后重试';
      setError(msg);
      await refreshCaptcha();
    }
  }

  // OAuth2 入口:标准授权码 + PKCE,浏览器顶层跳转到 IdP 登录页
  function handleSsoClick(p: OAuthProviderPublic) {
    oauthApi.redirectToProviderLogin(p.name);
  }

  return (
    <div className={styles.fullPage}>
      <Scale visible={cardVisible} duration={300}>
        <Card className={styles.card} style={{ width: '380px' }}>
          <div className={styles.cardBody}>
            <div className={styles.brand}>
              <img src="/icons/logo.svg" alt="Ming-Mail" className={styles.logo} />
              <Title3 as="h1" className={styles.title}>
                Ming-Mail
              </Title3>
            </div>

            {/* ── local:本地账号登录表单 + (可选)OAuth 圆形按钮 ── */}
            {loginConfig.local_login_enabled && (
              <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
              {/* 用户名 */}
              <div className={styles.field}>
                <Label htmlFor="login-username" required>
                  用户名 / 邮箱{' '}
                  <HelpPopover content="可用注册时的用户名,或绑定的邮箱登录,两者等效。" />
                </Label>
                <Input
                  id="login-username"
                  value={usernameOrEmail}
                  onChange={(_, d) => setUsernameOrEmail(d.value)}
                  onKeyDown={handleEnterKey}
                  contentBefore={<PersonRegular />}
                  placeholder="admin 或 admin@corp-a.test"
                  autoFocus
                />
                {fieldErrors.usernameOrEmail && (
                  <span className={styles.fieldError}>{fieldErrors.usernameOrEmail}</span>
                )}
              </div>

              {/* 密码 */}
              <div className={styles.field}>
                <Label htmlFor="login-password" required>
                  密码{' '}
                  <HelpPopover content="本地账号的登录密码。使用 OAuth / SSO 登录的用户请点击下方的第三方登录按钮。" />
                </Label>
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(_, d) => setPassword(d.value)}
                  onKeyDown={handleEnterKey}
                  contentBefore={<LockClosedRegular />}
                  placeholder="••••••"
                />
                {fieldErrors.password && (
                  <span className={styles.fieldError}>{fieldErrors.password}</span>
                )}
              </div>

              {error && <span className={styles.fieldError}>{error}</span>}

              {/* 登录按钮 = TeachingPopover trigger:校验通过后弹出验证码 */}
              <TeachingPopover
                open={captchaOpen}
                onOpenChange={handleCaptchaOpenChange}
                positioning="below"
              >
                <TeachingPopoverTrigger>
                  <Button
                    type="button"
                    appearance="primary"
                    icon={loading ? <Spinner size="tiny" appearance="inverted" /> : undefined}
                    className={styles.submitButton}
                  >
                    登录
                  </Button>
                </TeachingPopoverTrigger>

                <TeachingPopoverSurface className={styles.popoverSurface}>
                  <TeachingPopoverBody>
                    <TeachingPopoverTitle>滑块验证</TeachingPopoverTitle>
                    <SliderCaptcha
                      challenge={captcha}
                      onChange={setCaptchaX}
                      onRefresh={refreshCaptcha}
                      loading={captchaLoading}
                    />
                    {fieldErrors.captcha && (
                      <span className={styles.fieldError}>{fieldErrors.captcha}</span>
                    )}
                    {error && <span className={styles.fieldError}>{error}</span>}
                  </TeachingPopoverBody>
                  <div className={styles.popoverActions}>
                    <Button
                      appearance="subtle"
                      onClick={() => setCaptchaOpen(false)}
                      disabled={loading}
                    >
                      取消
                    </Button>
                    <Button
                      appearance="primary"
                      onClick={doLogin}
                      disabled={loading || !captcha}
                      icon={loading ? <Spinner size="tiny" appearance="inverted" /> : undefined}
                    >
                      确认登录
                    </Button>
                  </div>
                </TeachingPopoverSurface>
              </TeachingPopover>
            </form>
            )}

            {loginConfig.oauth_enabled && loginConfig.providers.length > 0 && (
              <div className={styles.ssoRow}>
                {loginConfig.providers.map((p) => (
                  <Button
                    key={p.name}
                    shape="circular"
                    appearance="outline"
                    className={styles.ssoCircle}
                    title={p.display_name || p.name}
                    aria-label={p.display_name || p.name}
                    onClick={() => handleSsoClick(p)}
                  >
                    <span className={styles.ssoCircleLabel}>
                      {p.name.charAt(0).toUpperCase() + p.name.slice(1)}
                    </span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </Card>
      </Scale>
    </div>
  );
}

export default LoginPage;